import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  IconButton,
  Modal,
  Snackbar,
  TextField,
  Tooltip,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Alert from '../../../components/common/Alert';
import Button from '../../../components/common/Button';
import ModalContent from '../../../components/common/Modal/ModalContent';
import Spinner from '../../../components/common/Spinner';
import TableSite from '../../../components/common/Table';
import PremioAPI from '../premio-api';
import { getJuradoAccessUrl } from '../premio-urls';
import { QRCodeCanvas } from 'qrcode.react';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return moment(dateString).format('DD-MM-YYYY');
};

const downloadCSV = (rows, filename) => {
  const header = Object.keys(rows[0]).join(',');
  const body = rows.map((row) =>
    Object.values(row)
      .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
      .join(',')
  );
  const csv = [header, ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const EMPTY_FORM = { name: '', email: '' };

// ─── Sub-nav ──────────────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'jurados', label: 'Jurados' },
  { key: 'finalistas', label: 'Finalistas' },
];

const SubNav = ({ active, onChange }) => (
  <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 24, paddingLeft: 8 }}>
    {SECTIONS.map(({ key, label }) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        style={{
          background: 'none',
          border: 'none',
          padding: '10px 24px',
          cursor: 'pointer',
          fontSize: 15,
          fontWeight: active === key ? 700 : 400,
          color: active === key ? '#1976d2' : '#555',
          borderBottom: active === key ? '2px solid #1976d2' : '2px solid transparent',
          marginBottom: -2,
          transition: 'color 0.15s',
        }}
      >
        {label}
      </button>
    ))}
  </div>
);

SubNav.propTypes = {
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// ─── Finalistas reorder + display name ────────────────────────────────────────
const FinalistasSection = ({ convocatoriaId, showSnackbar }) => {
  const [finalistas, setFinalistas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Per-row editing state: { [finalist_id]: { editing, draft, saving } }
  const [editingNames, setEditingNames] = useState({});

  const fetchFinalistas = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setIsLoading(true);
      const response = await PremioAPI.fetchFinalistasWithRanking(convocatoriaId);
      setFinalistas(response.data || []);
      setError('');
      setIsDirty(false);
    } catch (err) {
      setError(err?.data?.detail || 'No fue posible cargar los finalistas.');
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, [convocatoriaId]);

  useEffect(() => {
    fetchFinalistas(true);
    const interval = setInterval(() => fetchFinalistas(false), 5000);
    return () => clearInterval(interval);
  }, [fetchFinalistas]);

  const move = useCallback((index, direction) => {
    setFinalistas((prev) => {
      const next = [...prev];
      const swapIndex = index + direction;
      if (swapIndex < 0 || swapIndex >= next.length) return prev;
      [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
      return next;
    });
    setIsDirty(true);
  }, []);

  const handleSaveOrder = useCallback(async () => {
    try {
      setIsSaving(true);
      const orders = finalistas.map((f, i) => ({
        finalist_id: f.finalist_id,
        display_order: i + 1,
      }));
      await PremioAPI.updateFinalistasOrder(orders);
      setIsDirty(false);
      showSnackbar('Orden de finalistas guardado.', 'success');
    } catch (err) {
      showSnackbar(err?.data?.detail || 'No fue posible guardar el orden.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [finalistas, showSnackbar]);

  const startEditName = useCallback((f) => {
    setEditingNames((prev) => ({
      ...prev,
      [f.finalist_id]: { editing: true, draft: f.display_name || '', saving: false },
    }));
  }, []);

  const cancelEditName = useCallback((finalistId) => {
    setEditingNames((prev) => ({ ...prev, [finalistId]: { ...prev[finalistId], editing: false } }));
  }, []);

  const saveDisplayName = useCallback(async (finalistId) => {
    const row = editingNames[finalistId];
    if (!row) return;
    const newValue = row.draft.trim() || null;
    setEditingNames((prev) => ({ ...prev, [finalistId]: { ...prev[finalistId], saving: true } }));
    try {
      await PremioAPI.updateFinalistDisplayName(finalistId, newValue);
      setFinalistas((prev) =>
        prev.map((f) => f.finalist_id === finalistId ? { ...f, display_name: newValue } : f)
      );
      setEditingNames((prev) => ({ ...prev, [finalistId]: { editing: false, draft: newValue || '', saving: false } }));
      showSnackbar('Nombre actualizado.', 'success');
    } catch (err) {
      showSnackbar(err?.data?.detail || 'No fue posible actualizar el nombre.', 'error');
      setEditingNames((prev) => ({ ...prev, [finalistId]: { ...prev[finalistId], saving: false } }));
    }
  }, [editingNames, showSnackbar]);

  const clearDisplayName = useCallback(async (finalistId) => {
    setEditingNames((prev) => ({ ...prev, [finalistId]: { editing: false, draft: '', saving: true } }));
    try {
      await PremioAPI.updateFinalistDisplayName(finalistId, null);
      setFinalistas((prev) =>
        prev.map((f) => f.finalist_id === finalistId ? { ...f, display_name: null } : f)
      );
      showSnackbar('Nombre personalizado eliminado.', 'success');
    } catch (err) {
      showSnackbar(err?.data?.detail || 'No fue posible limpiar el nombre.', 'error');
    } finally {
      setEditingNames((prev) => ({ ...prev, [finalistId]: { ...prev[finalistId], saving: false } }));
    }
  }, [showSnackbar]);

  if (isLoading) return <Spinner size={40} style={{ marginTop: 32 }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="actions-table__title" style={{ margin: 0 }}>Finalistas</h1>
        <Button onClick={handleSaveOrder} loading={isSaving} disabled={!isDirty}>
          Guardar orden
        </Button>
      </div>
      <p style={{ margin: '0 0 20px', color: '#666', fontSize: 13 }}>
        Define el orden y el nombre que verán los jurados. Si el nombre a mostrar está vacío, se usa el nombre original de la postulación.
      </p>
      {error && <Alert type="error">{error}</Alert>}
      {finalistas.length === 0 ? (
        <p style={{ color: '#999', textAlign: 'center', padding: '32px 0' }}>
          No hay finalistas registrados aún.
        </p>
      ) : (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
          {finalistas.map((f, i) => {
            const rowEdit = editingNames[f.finalist_id] || {};
            const hasCustomName = f.display_name != null && f.display_name !== '';

            return (
              <div
                key={f.finalist_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '14px 20px',
                  borderBottom: i < finalistas.length - 1 ? '1px solid #f0f0f0' : 'none',
                  background: i % 2 === 0 ? '#fff' : '#fafafa',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                {/* Position */}
                <span style={{ width: 24, fontWeight: 700, color: '#bbb', fontSize: 13, flexShrink: 0 }}>
                  {i + 1}
                </span>

                {/* Names column */}
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 2 }}>Nombre original</div>
                  <div style={{ fontSize: 14, color: '#444' }}>{f.original_name}</div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 6, marginBottom: 2 }}>Nombre a mostrar</div>
                  {rowEdit.editing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <TextField
                        value={rowEdit.draft || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditingNames((prev) => ({
                            ...prev,
                            [f.finalist_id]: { ...prev[f.finalist_id], draft: value },
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveDisplayName(f.finalist_id);
                          if (e.key === 'Escape') cancelEditName(f.finalist_id);
                        }}
                        placeholder={f.original_name}
                        variant="outlined"
                        size="small"
                        disabled={rowEdit.saving}
                        style={{ minWidth: 220 }}
                      />
                      <Button onClick={() => saveDisplayName(f.finalist_id)} loading={rowEdit.saving} style={{ minWidth: 80 }}>
                        Guardar
                      </Button>
                      <Button secondary onClick={() => cancelEditName(f.finalist_id)} disabled={rowEdit.saving}>
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: hasCustomName ? 600 : 400, color: hasCustomName ? '#1976d2' : '#bbb', fontStyle: hasCustomName ? 'normal' : 'italic' }}>
                        {hasCustomName ? f.display_name : 'Sin nombre personalizado'}
                      </span>
                      <Tooltip title="Editar nombre a mostrar">
                        <IconButton size="small" onClick={() => startEditName(f)} disabled={rowEdit.saving}>
                          <span className="material-icons" style={{ fontSize: 16 }}>edit</span>
                        </IconButton>
                      </Tooltip>
                      {hasCustomName && (
                        <Tooltip title="Volver al nombre original">
                          <IconButton size="small" onClick={() => clearDisplayName(f.finalist_id)} disabled={rowEdit.saving}>
                            <span className="material-icons" style={{ fontSize: 16, color: '#e53935' }}>clear</span>
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>

                {/* Score */}
                {f.average_score != null ? (
                  <span style={{ fontSize: 13, color: '#1976d2', fontWeight: 700, minWidth: 90, textAlign: 'right' }}>
                    ★ {f.average_score.toFixed(2)} <span style={{ fontWeight: 400, color: '#888' }}>/ 5</span>
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: '#bbb', minWidth: 90, textAlign: 'right' }}>Sin notas</span>
                )}

                {/* Rankings */}
                <span style={{ fontSize: 12, color: '#aaa', minWidth: 100, textAlign: 'right' }}>
                  {f.rankings_confirmed > 0
                    ? `${f.rankings_confirmed} ranking${f.rankings_confirmed !== 1 ? 's' : ''}`
                    : ''}
                </span>

                {/* Order arrows */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Tooltip title="Subir">
                    <span>
                      <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}>
                        <span className="material-icons" style={{ fontSize: 16 }}>arrow_upward</span>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Bajar">
                    <span>
                      <IconButton size="small" onClick={() => move(i, 1)} disabled={i === finalistas.length - 1}>
                        <span className="material-icons" style={{ fontSize: 16 }}>arrow_downward</span>
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

FinalistasSection.propTypes = {
  convocatoriaId: PropTypes.string.isRequired,
  showSnackbar: PropTypes.func.isRequired,
};

// ─── Jurados section ──────────────────────────────────────────────────────────
const JuradosSection = ({ convocatoriaId, showSnackbar }) => {
  const [jurados, setJurados] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isCreating, setIsCreating] = useState(false);

  const [newCredentials, setNewCredentials] = useState(null);
  const [sessionCredentials, setSessionCredentials] = useState([]);
  const [accessLink, setAccessLink] = useState(null);
  const [accessLinkCopied, setAccessLinkCopied] = useState(false);

  const [juradoToDelete, setJuradoToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchJurados = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setIsLoading(true);
      const response = await PremioAPI.fetchJurados(convocatoriaId);
      setJurados(response.data || []);
      setError('');
    } catch (err) {
      setError(err?.data?.detail || 'No fue posible cargar los jurados.');
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, [convocatoriaId]);

  useEffect(() => {
    fetchJurados(true);
    const interval = setInterval(() => fetchJurados(false), 5000);
    return () => clearInterval(interval);
  }, [fetchJurados]);

  const handleOpenAddModal = () => {
    setForm(EMPTY_FORM);
    setIsAddModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateJurado = useCallback(async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      setIsCreating(true);
      const response = await PremioAPI.createJurado({
        name: form.name.trim(),
        email: form.email.trim(),
        convocatoria_id: convocatoriaId,
      });
      const { juror, plain_password } = response.data;
      setJurados((prev) => [...prev, juror]);
      const cred = { nombre: juror.name, email: juror.email, contraseña: plain_password };
      setSessionCredentials((prev) => [...prev, cred]);
      setNewCredentials({ name: juror.name, email: juror.email, password: plain_password });
      setIsAddModalOpen(false);
    } catch (err) {
      showSnackbar(err?.data?.detail || 'No fue posible agregar el jurado.', 'error');
    } finally {
      setIsCreating(false);
    }
  }, [convocatoriaId, form, showSnackbar]);

  const handleConfirmDelete = useCallback(async () => {
    if (!juradoToDelete) return;
    try {
      setIsDeleting(true);
      await PremioAPI.deleteJurado(juradoToDelete.id);
      setJurados((prev) =>
        prev.map((j) => (j.id === juradoToDelete.id ? { ...j, is_active: false } : j))
      );
      showSnackbar('Jurado desactivado correctamente.', 'success');
      setJuradoToDelete(null);
    } catch (err) {
      showSnackbar(err?.data?.detail || 'No fue posible desactivar el jurado.', 'error');
    } finally {
      setIsDeleting(false);
    }
  }, [juradoToDelete, showSnackbar]);

  // El token se pide al abrir el modal y no al listar: son credenciales, y
  // traerlas para toda la tabla las dejaría en memoria del navegador sin que
  // nadie las haya pedido.
  const handleOpenAccessLink = useCallback(
    async (jurado) => {
      setAccessLinkCopied(false);
      setAccessLink({ jurado, url: null, expiresAt: null, error: null });
      try {
        const response = await PremioAPI.fetchJuradoAccessLink(jurado.id);
        const { token, expires_at: expiresAt, convocatoria_slug: slug } = response.data;
        setAccessLink({
          jurado,
          url: getJuradoAccessUrl(slug, token),
          expiresAt,
          error: null,
        });
      } catch (err) {
        setAccessLink({
          jurado,
          url: null,
          expiresAt: null,
          error: err?.data?.detail || 'No fue posible generar el enlace.',
        });
      }
    },
    []
  );

  // El canvas del QR se exporta a PNG para poder mandarlo por correo o
  // mensajería, que es como se reparte en la práctica.
  const handleDownloadQr = useCallback(() => {
    const canvas = document.getElementById('qr-acceso-jurado');
    if (!canvas) return;
    const enlace = document.createElement('a');
    enlace.href = canvas.toDataURL('image/png');
    enlace.download = `acceso-${(accessLink?.jurado?.email || 'jurado').replace(/[^a-z0-9]+/gi, '-')}.png`;
    enlace.click();
  }, [accessLink]);

  const handleCopyAccessLink = useCallback(() => {
    if (!accessLink?.url) return;
    navigator.clipboard.writeText(accessLink.url);
    setAccessLinkCopied(true);
  }, [accessLink]);

  const handleDownloadSessionCredentials = useCallback(() => {
    if (!sessionCredentials.length) return;
    downloadCSV(sessionCredentials, `jurados-credenciales-${convocatoriaId}.csv`);
  }, [sessionCredentials, convocatoriaId]);

  const handleDownloadSingleCredential = useCallback(() => {
    if (!newCredentials) return;
    downloadCSV(
      [{ nombre: newCredentials.name, email: newCredentials.email, contraseña: newCredentials.password }],
      `jurado-${newCredentials.email}.csv`
    );
  }, [newCredentials]);

  const handleCopyCredentials = useCallback(() => {
    if (!newCredentials) return;
    const text = `Nombre: ${newCredentials.name}\nEmail: ${newCredentials.email}\nContraseña: ${newCredentials.password}`;
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar('Credenciales copiadas al portapapeles.', 'success');
    });
  }, [newCredentials, showSnackbar]);

  const juradosColumns = useMemo(
    () => [
      { Header: 'Nombre', accessor: 'name' },
      { Header: 'Email', accessor: 'email' },
      {
        Header: 'Evaluaciones',
        accessor: 'evaluations_count',
        Cell: ({ value }) => (
          <span style={{ fontWeight: 500 }}>{value} finalista{value !== 1 ? 's' : ''}</span>
        ),
      },
      {
        Header: 'Ranking enviado',
        accessor: 'has_confirmed_ranking',
        Cell: ({ value }) => (
          <span style={{ color: value ? '#27ae60' : '#e67e22', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span className="material-icons" style={{ fontSize: 16 }}>{value ? 'check_circle' : 'schedule'}</span>
            {value ? 'Enviado' : 'Pendiente'}
          </span>
        ),
      },
      {
        Header: 'Cuenta',
        accessor: 'is_active',
        Cell: ({ value }) => (
          <span style={{ color: value ? '#27ae60' : '#c0392b', fontWeight: 600 }}>
            {value ? 'Activo' : 'Inactivo'}
          </span>
        ),
      },
      { Header: 'Creado', accessor: 'created_at', Cell: ({ value }) => formatDate(value) },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row }) =>
          row.original.is_active ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <Tooltip title="Enlace de acceso">
                <IconButton size="small" onClick={() => handleOpenAccessLink(row.original)}>
                  <span className="material-icons" style={{ fontSize: 18 }}>link</span>
                </IconButton>
              </Tooltip>
              <Tooltip title="Desactivar jurado">
                <IconButton size="small" onClick={() => setJuradoToDelete(row.original)}>
                  <span className="material-icons" style={{ fontSize: 18 }}>person_off</span>
                </IconButton>
              </Tooltip>
            </div>
          ) : null,
      },
    ],
    [handleOpenAccessLink]
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <h1 className="actions-table__title" style={{ margin: 0 }}>Jurados</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {sessionCredentials.length > 0 && (
            <Button secondary onClick={handleDownloadSessionCredentials}>
              Descargar credenciales ({sessionCredentials.length})
            </Button>
          )}
          <Button onClick={handleOpenAddModal}>+ Agregar jurado</Button>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}
      <TableSite data={jurados} columns={juradosColumns} isLoadingData={isLoading} tableName="" />

      {/* Modal: agregar */}
      {isAddModalOpen && (
        <Modal open onClose={() => setIsAddModalOpen(false)}>
          <ModalContent title="Agregar jurado" onClick={() => setIsAddModalOpen(false)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
              <TextField
                label="Nombre completo"
                name="name"
                value={form.name}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                autoFocus
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
              />
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                La contraseña se generará automáticamente y se mostrará una sola vez.
              </p>
            </div>
            <div className="modal__action-container">
              <Button secondary onClick={() => setIsAddModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateJurado} loading={isCreating} disabled={!form.name.trim() || !form.email.trim()}>
                Agregar
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Modal: credenciales */}
      {newCredentials && (
        <Modal open onClose={() => setNewCredentials(null)}>
          <ModalContent title="Jurado creado" onClick={() => setNewCredentials(null)}>
            <p style={{ marginTop: 0 }}>
              Guarda estas credenciales — la contraseña no se volverá a mostrar.
            </p>
            <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '12px 16px', fontFamily: 'monospace', fontSize: 14, lineHeight: 2, marginBottom: 20 }}>
              <div><strong>Nombre:</strong> {newCredentials.name}</div>
              <div><strong>Email:</strong> {newCredentials.email}</div>
              <div><strong>Contraseña:</strong> {newCredentials.password}</div>
            </div>
            <div className="modal__action-container">
              <Button secondary onClick={handleCopyCredentials}>Copiar</Button>
              <Button secondary onClick={handleDownloadSingleCredential}>Descargar</Button>
              <Button onClick={() => setNewCredentials(null)}>Listo</Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Modal: enlace de acceso */}
      {accessLink && (
        <Modal open onClose={() => setAccessLink(null)}>
          <ModalContent
            title="Enlace de acceso"
            onClick={() => setAccessLink(null)}
            contentStyle={{ maxWidth: 'min(420px, 88vw)' }}
          >
            <p style={{ marginTop: 0 }}>
              Con este enlace <strong>{accessLink.jurado.name}</strong> entra al portal
              sin escribir credenciales. Puedes enviárselo o mostrarle el código.
            </p>

            {accessLink.error && <Alert type="error">{accessLink.error}</Alert>}

            {!accessLink.url && !accessLink.error && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spinner />
              </div>
            )}

            {accessLink.url && (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                  <QRCodeCanvas
                    id="qr-acceso-jurado"
                    value={accessLink.url}
                    size={192}
                    includeMargin
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </div>

                <div
                  style={{
                    background: '#f5f5f5',
                    borderRadius: 6,
                    padding: '12px 16px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    marginBottom: 12,
                  }}
                >
                  {accessLink.url}
                </div>

                <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
                  Vence el {formatDate(accessLink.expiresAt)}. Deja de servir antes si
                  desactivas al jurado.
                </p>
              </>
            )}

            {/* Flex y no la grilla de dos columnas de `modal__action-container`:
                son tres botones, y en pantallas angostas tienen que envolver. */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                justifyContent: 'flex-end',
                marginTop: 16,
              }}
            >
              <Button secondary onClick={handleCopyAccessLink} disabled={!accessLink.url}>
                {accessLinkCopied ? 'Copiado' : 'Copiar enlace'}
              </Button>
              <Button secondary onClick={handleDownloadQr} disabled={!accessLink.url}>
                Descargar QR
              </Button>
              <Button onClick={() => setAccessLink(null)}>Listo</Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Modal: confirmar desactivación */}
      {juradoToDelete && (
        <Modal open onClose={() => setJuradoToDelete(null)}>
          <ModalContent title="Desactivar jurado" onClick={() => setJuradoToDelete(null)}>
            <p>
              ¿Deseas desactivar a <strong>{juradoToDelete.name}</strong>? No podrá iniciar sesión en el portal de jurados.
            </p>
            <div className="modal__action-container">
              <Button secondary onClick={() => setJuradoToDelete(null)}>Cancelar</Button>
              <Button onClick={handleConfirmDelete} loading={isDeleting}>Desactivar</Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

JuradosSection.propTypes = {
  convocatoriaId: PropTypes.string.isRequired,
  showSnackbar: PropTypes.func.isRequired,
};

// ─── FinalTab root ────────────────────────────────────────────────────────────
const FinalTab = ({ convocatoriaId }) => {
  const [activeSection, setActiveSection] = useState('jurados');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <div style={{ paddingTop: 8 }}>
      <SubNav active={activeSection} onChange={setActiveSection} />

      <div style={{ padding: '0 24px 24px' }}>
        {activeSection === 'jurados' && (
          <JuradosSection convocatoriaId={convocatoriaId} showSnackbar={showSnackbar} />
        )}
        {activeSection === 'finalistas' && (
          <FinalistasSection convocatoriaId={convocatoriaId} showSnackbar={showSnackbar} />
        )}
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} elevation={6} variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

FinalTab.propTypes = {
  convocatoriaId: PropTypes.string.isRequired,
};

export default FinalTab;
